import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";

interface DismissRecommendationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * AUT-460: a never-prioritized (Recommended) card reads "dismissing" / "Dismiss & save
   * feedback"; a prioritized card's Remove strategy keeps "removing" / "Remove & save
   * feedback" -- the two are conceptually different actions per the brief even though
   * everything else in the dialog (description, reasons, notes, Cancel, pending/disabled
   * and reset behaviour) is identical.
   */
  kind: "dismiss" | "remove";
  gapTitle: string;
  /** Reason list comes from the API so it cannot drift from the server's own list. */
  reasons: string[];
  isPending?: boolean;
  onConfirm: (reason: string, notes?: string) => void;
}

/**
 * Serves both `Dismiss` (Other strategies → Recommended) and `Remove strategy`
 * (Prioritized strategies → kebab) — one dialog, one Brand Context write.
 *
 * A reason is always selected (the first in the server's list), so there is no
 * disabled-until-chosen state. State is only reset on the *next* opening — while `open`
 * stays `true` after a failed submit, the chosen reason and typed text are kept, and
 * `Cancel` never resets them either.
 */
export function DismissRecommendationDialog({
  open,
  onOpenChange,
  kind,
  gapTitle,
  reasons,
  isPending = false,
  onConfirm,
}: DismissRecommendationDialogProps) {
  const [reason, setReason] = useState(reasons[0] ?? "");
  const [notes, setNotes] = useState("");

  // Reset on each opening only -- `open` staying `true` (kept open after a failed
  // request) must not clear what the person already chose or typed.
  useEffect(() => {
    if (open) {
      setReason(reasons[0] ?? "");
      setNotes("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleConfirm = () => {
    const trimmed = notes.trim();
    onConfirm(reason, trimmed === "" ? undefined : trimmed);
  };

  const title =
    kind === "dismiss" ? "Why are you dismissing this strategy?" : "Why are you removing this strategy?";
  const confirmLabel = kind === "dismiss" ? "Dismiss & save feedback" : "Remove & save feedback";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Your feedback is saved to Brand Context so Signal AI can improve future recommendations
            for this brand.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-sm border border-border bg-muted/40 px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Strategy name
            </p>
            <p className="mt-0.5 text-sm text-foreground">{gapTitle}</p>
          </div>

          <div className="space-y-2">
            <Label>Reason</Label>
            <RadioGroup value={reason} onValueChange={setReason} className="space-y-1.5">
              {reasons.map((option, index) => (
                <div key={option} className="flex items-start gap-2">
                  <RadioGroupItem
                    value={option}
                    id={`dismiss-reason-${index}`}
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor={`dismiss-reason-${index}`}
                    className="text-sm font-normal leading-snug cursor-pointer"
                  >
                    {option}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="dismiss-notes">
              Additional context <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="dismiss-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="e.g. We've had legal push back on the phrase 'clinically proven' — avoid similar language."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="default" disabled={isPending} onClick={handleConfirm}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
